import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { postMastra } from '@/lib/mastra';

export interface TrainerClientSummary {
  id: string;
  display_name: string | null;
  email: string | null;
  fitness_level: string | null;
  status: string;
  compliance_rate: number | null;
  last_workout_at: string | null;
  /** Latest performed product date, shared with client details. */
  last_workout_date: string | null;
  next_scheduled_date: string | null;
  program_name: string | null;
  consecutive_missed: number;
  /** Null when the count could not be read. */
  form_checks_to_review?: number | null;
}

interface TrainerClientsResponse {
  clients: TrainerClientSummary[];
  total: number;
  nextCursor?: string | null;
  success: boolean;
  message?: string;
}

export function useTrainerClients() {
  const { user, session } = useAuth();

  return useQuery({
    queryKey: ['trainer-clients', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('Not authenticated');
      const clients: TrainerClientSummary[] = [];
      let cursor: string | null = null;
      do {
        const payload: TrainerClientsResponse = await postMastra<TrainerClientsResponse>(
          '/trainer/clients',
          { resourceId: user.id, status: 'active', cursor },
          { accessToken: session?.access_token },
        );

        if (!payload.success) {
          throw new Error(payload.message ?? 'Failed to load clients');
        }

        clients.push(...payload.clients);
        cursor = payload.nextCursor ?? null;
      } while (cursor);

      return clients;
    },
    enabled: !!user?.id,
  });
}
